import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { Preferences } from "@capacitor/preferences";
import { isNative } from "@/native/capacitor";
import { requestAndRegister, getPushPermission } from "@/native/push";

export const SNOOZE_KEY = "leadctrl_push_prompt_snoozed_until";
export const SNOOZE_DAYS = 14;
const ASKABLE = ["prompt", "prompt-with-rationale"];

/**
 * Contextual push-permission card on Today (after sign-in), native iOS only. Never at boot, never
 * when permission is already granted/denied; only "Enable" triggers the OS dialog.
 */
export function NotificationPermissionPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNative()) return;
      if (!ASKABLE.includes(await getPushPermission())) return;
      try {
        const { value } = await Preferences.get({ key: SNOOZE_KEY });
        if (value && Number(value) > Date.now()) return;
      } catch { /* treat as not snoozed */ }
      if (alive) setVisible(true);
    })();
    return () => { alive = false; };
  }, []);

  const accept = async () => {
    setVisible(false);
    const res = await requestAndRegister();
    if (res.ok) toast.success("Notifications turned on");
    else if (res.reason === "denied") toast("You can turn notifications on later in Settings");
  };

  const snooze = async () => {
    setVisible(false);
    try {
      await Preferences.set({ key: SNOOZE_KEY, value: String(Date.now() + SNOOZE_DAYS * 86400000) });
    } catch { /* ignore */ }
  };

  if (!visible) return null;
  return (
    <div data-testid="notif-permission-prompt"
      className="mt-4 flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50/70 p-4">
      <span className="mt-0.5 rounded-lg bg-teal-600 p-2 text-white"><Bell className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-800">Get alerts for new website chats, leads and follow-ups</p>
        <p className="mt-1 text-xs text-slate-500">You can change this anytime in Settings.</p>
        <div className="mt-3 flex gap-2">
          <button data-testid="notif-prompt-accept" onClick={accept}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-teal-700 px-4 text-sm font-bold text-white hover:bg-teal-800">Enable</button>
          <button data-testid="notif-prompt-dismiss" onClick={snooze}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Not now</button>
        </div>
      </div>
      <button data-testid="notif-prompt-close" onClick={snooze} aria-label="Not now" className="shrink-0 rounded p-1 text-slate-300 hover:text-slate-500"><X className="h-4 w-4" /></button>
    </div>
  );
}

// One-line hint for Notification preferences when iOS permission was denied.
export function PushDeniedHint() {
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    if (!isNative()) return;
    getPushPermission().then((p) => setDenied(p === "denied"));
  }, []);
  if (!denied) return null;
  return (
    <p data-testid="push-denied-hint" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      Notifications are off for LeadCTRL. Turn on in Settings → Notifications → LeadCTRL.
    </p>
  );
}
